// src/pages/client/StylistDetailsPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMarketplacePaths } from '../../utils/guestAccess';
import stylistService from '../../services/stylistService';
import { Stylist } from '../../types/stylist';
import StylistHeader from '../../components/client/StylistHeader';
import reviewService from '../../services/reviewService';
import {
  StylistLiveReviewSummary,
  getEmbeddedStylistRating,
  ratingFromLiveSummary,
} from '../../utils/stylistLiveReviewSummary';
import StylistServices from '../../components/client/StylistServices';
import StylistReviews from '../../components/client/StylistReview';
import StylistPortfolio from '../../components/client/StylistPortfolio';
import StylistPolicies, {
  fetchStylistPolicies,
  hasAcknowledgedPolicies,
} from '../../components/client/StylistPolicies';

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', color: '#E1306C', textColor: '#FFF', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>' },
  { key: 'tiktok',    label: 'TikTok',    color: '#010101', textColor: '#FFF', svg: null },
  { key: 'facebook',  label: 'Facebook',  color: '#1877F2', textColor: '#FFF', svg: null },
  { key: 'twitter',   label: 'X (Twitter)', color: '#1DA1F2', textColor: '#FFF', svg: null },
  { key: 'youtube',   label: 'YouTube',   color: '#FF0000', textColor: '#FFF', svg: null },
  { key: 'threads',   label: 'Threads',   color: '#101010', textColor: '#FFF', svg: null },
  { key: 'pinterest', label: 'Pinterest', color: '#E60023', textColor: '#FFF', svg: null },
  { key: 'snapchat',  label: 'Snapchat',  color: '#FFCC00', textColor: '#333', svg: null },
  { key: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2', textColor: '#FFF', svg: null },
  { key: 'website',   label: 'Website',   color: '#C9A84C', textColor: '#FFF', svg: null },
];

const safeUrl = (url: string | null | undefined): string | undefined => {
  if (!url || typeof url !== 'string') return undefined;
  const normalized = url
    .replace(/&#x2F;/gi, '/')
    .replace(/&#47;/g, '/')
    .replace(/&amp;/g, '&')
    .trim();
  const withProtocol = /^https?:\/\//i.test(normalized)
    ? normalized
    : normalized
      ? `https://${normalized}`
      : '';
  return /^https?:\/\/.+\..+/i.test(withProtocol) ? withProtocol : undefined;
};

const StylistSocialMedia: React.FC<{ stylist: Stylist }> = ({ stylist }) => {
  const socialMedia = (stylist as any).socialMedia || {};
  // Only show platforms that have a safe (http/https) URL
  const active = SOCIAL_PLATFORMS.filter(p => safeUrl(socialMedia[p.key]));

  if (active.length === 0) {
    return (
      <div className="stylist-social__empty">
        <p>No social media links have been added yet.</p>
      </div>
    );
  }

  return (
    <div className="stylist-social__grid">
      {active.map(platform => (
        <a
          key={platform.key}
          href={safeUrl(socialMedia[platform.key])}
          target="_blank"
          rel="noopener noreferrer"
          className="stylist-social__card"
          style={{ backgroundColor: platform.color, color: platform.textColor }}
        >
          <span className="stylist-social__label">{platform.label}</span>
          <span className="stylist-social__arrow">↗</span>
        </a>
      ))}
    </div>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stylist-tabpanel-${index}`}
      aria-labelledby={`stylist-tab-${index}`}
      className={`stylist-details__tab-panel ${value === index ? 'stylist-details__tab-panel--active' : ''}`}
    >
      {children}
    </div>
  );
};

const StylistDetailsPage: React.FC = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const paths = useMarketplacePaths();
  const id = shortCode;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Try to get stylist from location state first (passed from BrowseStylesPage)
  const passedStylist = location.state?.stylist as Stylist | undefined;
  
  const [stylist, setStylist] = useState<Stylist | null>(passedStylist || null);
  const [loading, setLoading] = useState<boolean>(passedStylist ? false : true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [liveReviewSummary, setLiveReviewSummary] = useState<StylistLiveReviewSummary>('loading');
  const [showPoliciesTab, setShowPoliciesTab] = useState(false);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    // Only fetch details if we don't already have the stylist data from location state
    if (!passedStylist && id) {
      fetchStylistDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, passedStylist]); // fetchStylistDetails is stable

  // Published review totals from the reviews API (stylist profile payload often omits counts).
  useEffect(() => {
    if (!stylist?._id) return;
    let cancelled = false;
    setLiveReviewSummary('loading');
    reviewService
      .getStylistReviews(stylist._id, { page: 1, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const count = res.stats?.totalReviews ?? res.pagination?.total ?? 0;
        const average = Number(res.stats?.averageRating ?? 0);
        setLiveReviewSummary({ count, average });
      });
    return () => {
      cancelled = true;
    };
  }, [stylist?._id]);

  // Gate Policies tab: only when at least one policy has acknowledgedAt (same as mobile).
  useEffect(() => {
    if (!stylist?._id) {
      setShowPoliciesTab(false);
      return;
    }
    let cancelled = false;
    fetchStylistPolicies(stylist._id)
      .then((data) => {
        if (cancelled) return;
        const show = hasAcknowledgedPolicies(data);
        setShowPoliciesTab(show);
        // Policies is index 2 when shown; later tabs shift down when it disappears
        setTabValue((prev) => {
          if (show) return prev;
          if (prev === 2) return 0; // was on Policies
          if (prev === 3) return 2; // was on Reviews
          if (prev === 4) return 3; // was on Follow Us
          return prev;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setShowPoliciesTab(false);
        setTabValue((prev) => {
          if (prev === 2) return 0;
          if (prev === 3) return 2;
          if (prev === 4) return 3;
          return prev;
        });
      });
    return () => {
      cancelled = true;
    };
  }, [stylist?._id]);
  
  const fetchStylistDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!id) {
        setError('Stylist ID is missing');
        setLoading(false);
        return;
      }
      
      const stylistData = await stylistService.getStylist(id);
      setStylist(stylistData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stylist details:', err);
      setError('Failed to load stylist details');
      setLoading(false);
    }
  };
  
  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };
  
  // Scroll to the services tab so the user can pick a service before booking
  const handleBookNowClick = () => {
    setTabValue(0);
    const tabsEl = document.querySelector('.stylist-details__tabs');
    if (tabsEl) {
      tabsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  // Handle service selection from the services tab
  const handleSelectService = (serviceId: string, serviceDetails?: any) => {
    if (!stylist || !serviceId || !serviceDetails) return;
    
    navigate(paths.booking(shortCode || (stylist as any).shortCode || stylist._id), {
      state: {
        stylistId: stylist._id,
        serviceId: serviceId,
        service: {
          name: serviceDetails.service.name,
          price: serviceDetails.service.basePrice.min,
          duration: serviceDetails.service.baseDuration.min,
          description: serviceDetails.service.description?.short
        },
        stylist: {
          name: stylist.name,
          profilePhoto: typeof stylist.profilePhoto === 'object' ? stylist.profilePhoto.url : stylist.profilePhoto
        }
      }
    });
  };
  
  if (loading) {
    return (
      <div className="stylist-details">
        <div className="container">
          <div className="stylist-details__loading">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !stylist) {
    return (
      <div className="stylist-details">
        <div className="container">
          <button className="stylist-details__back-button" onClick={() => navigate(-1)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back
          </button>
          
          <div className="stylist-details__error">
            <svg className="stylist-details__error-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div className="stylist-details__error-message">
              {error || 'Stylist not found'}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const embedded = getEmbeddedStylistRating(stylist);
  const { average: ratingAvg, count: ratingCount, loading: summaryLoading } =
    ratingFromLiveSummary(embedded, liveReviewSummary);

  return (
    <div className="stylist-details">
      <div className="container">
        <button className="stylist-details__back-button" onClick={() => navigate(-1)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </button>
        
        {/* Stylist Header */}
        <StylistHeader stylist={stylist} liveReviewSummary={liveReviewSummary} />
        
        {/* Book Now CTA - Shown at top on mobile */}
        {isMobile && (
          <div className="stylist-details__book-mobile">
            <button className="stylist-details__book-button" onClick={handleBookNowClick}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Book Now
            </button>
          </div>
        )}
        
        <div className="stylist-details__content">
          {/* Main Content */}
          <div className="stylist-details__main-content">
            <div className="stylist-details__tabs">
              <div className="stylist-details__tabs-header">
                <button
                  className={`stylist-details__tab ${tabValue === 0 ? 'stylist-details__tab--active' : ''}`}
                  onClick={() => handleTabChange(0)}
                  id="stylist-tab-0"
                  aria-controls="stylist-tabpanel-0"
                >
                  Services
                </button>
                <button
                  className={`stylist-details__tab ${tabValue === 1 ? 'stylist-details__tab--active' : ''}`}
                  onClick={() => handleTabChange(1)}
                  id="stylist-tab-1"
                  aria-controls="stylist-tabpanel-1"
                >
                  Portfolio
                </button>
                {showPoliciesTab && (
                  <button
                    className={`stylist-details__tab ${tabValue === 2 ? 'stylist-details__tab--active' : ''}`}
                    onClick={() => handleTabChange(2)}
                    id="stylist-tab-2"
                    aria-controls="stylist-tabpanel-2"
                  >
                    Policies
                  </button>
                )}
                <button
                  className={`stylist-details__tab ${tabValue === (showPoliciesTab ? 3 : 2) ? 'stylist-details__tab--active' : ''}`}
                  onClick={() => handleTabChange(showPoliciesTab ? 3 : 2)}
                  id={`stylist-tab-${showPoliciesTab ? 3 : 2}`}
                  aria-controls={`stylist-tabpanel-${showPoliciesTab ? 3 : 2}`}
                >
                  Reviews
                </button>
                <button
                  className={`stylist-details__tab ${tabValue === (showPoliciesTab ? 4 : 3) ? 'stylist-details__tab--active' : ''}`}
                  onClick={() => handleTabChange(showPoliciesTab ? 4 : 3)}
                  id={`stylist-tab-${showPoliciesTab ? 4 : 3}`}
                  aria-controls={`stylist-tabpanel-${showPoliciesTab ? 4 : 3}`}
                >
                  Follow Us
                </button>
              </div>
              
              <TabPanel value={tabValue} index={0}>
                <StylistServices stylistId={stylist._id} onSelectService={handleSelectService} />
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <StylistPortfolio stylistId={stylist._id} />
              </TabPanel>

              {showPoliciesTab && (
                <TabPanel value={tabValue} index={2}>
                  <StylistPolicies stylistId={stylist._id} />
                </TabPanel>
              )}
              
              <TabPanel value={tabValue} index={showPoliciesTab ? 3 : 2}>
                <StylistReviews stylistId={stylist._id} />
              </TabPanel>

              <TabPanel value={tabValue} index={showPoliciesTab ? 4 : 3}>
                <StylistSocialMedia stylist={stylist} />
              </TabPanel>
            </div>
          </div>
          
          {/* Sidebar - Hidden on mobile */}
          <div className="stylist-details__sidebar">
            <div className="stylist-details__booking-card">
              <h3 className="stylist-details__booking-title">Book an Appointment</h3>
              
              <p className="stylist-details__booking-description">
                Select from {stylist.name}'s services and book your appointment now.
              </p>
              
              <button className="stylist-details__book-button" onClick={handleBookNowClick}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Book Now
              </button>
              
              <div className="stylist-details__divider"></div>
              
              <div className="stylist-details__stats-item">
                <div className="stylist-details__stats-label">Rating</div>
                <div className="stylist-details__rating">
                {summaryLoading ? (
                  <span
                    className="stylist-details__rating-none"
                    aria-busy="true"
                    aria-live="polite"
                  >
                    …
                  </span>
                ) : ratingCount > 0 ? (
                  <>
                    <div className="stylist-details__rating-stars">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                    </div>
                    <span className="stylist-details__rating-value">
                      {ratingAvg.toFixed(1)}
                    </span>
                    <span className="stylist-details__rating-count">
                      ({ratingCount} reviews)
                    </span>
                  </>
                ) : (
                  <span className="stylist-details__rating-none">No reviews yet</span>
                )}
                </div>
              </div>
              
              {stylist.location && (
                <div className="stylist-details__stats-item">
                  <div className="stylist-details__stats-label">Location</div>
                  <div className="stylist-details__location">
                    {stylist.location.city}, {stylist.location.state}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StylistDetailsPage;